import { IBlobEvent, IMediaRecorder } from 'extendable-media-recorder';

import { MediaRecorderType } from '../../types/audio.types.ts';

export interface MultiAudioRecorderCallbacks {
  ondataavailable: (type: MediaRecorderType, event: IBlobEvent) => void;
  onstop: (audioBlobs: Record<MediaRecorderType, Blob>) => void;
  onerror: () => void;
}

export class MultiAudioRecorderUtil {
  private readonly stoppedRecorders = new Set<MediaRecorderType>();
  private readonly audioChunks: Record<MediaRecorderType, Blob[]>;

  private readonly mediaRecordersStopped: Promise<void>;
  private mediaRecordersStoppedResolve: (() => void) | null = null;

  constructor(private readonly mediaRecorders: Record<MediaRecorderType, IMediaRecorder>) {
    this.audioChunks = this.createEmptyAudioChunks();

    this.mediaRecordersStopped = new Promise((resolve) => {
      this.mediaRecordersStoppedResolve = resolve;
    });
  }

  public init(callbacks: MultiAudioRecorderCallbacks): void {
    Object.entries(this.mediaRecorders).forEach(([type, recorder]) => {
      const mediaRecorderType = type as MediaRecorderType;

      recorder.ondataavailable = (event: IBlobEvent): void => {
        this.audioChunks[mediaRecorderType].push(event.data);
        callbacks.ondataavailable(mediaRecorderType, event);
      };

      recorder.onstop = (): void => {
        this.handleRecorderStop(mediaRecorderType, callbacks);
      };

      recorder.onerror = (): void => {
        callbacks.onerror();
      };
    });
  }

  public start(timeSlice: number): void {
    this.stoppedRecorders.clear();
    Object.values(this.mediaRecorders).forEach((recorder) => {
      recorder.start(timeSlice);
    });
  }

  public stop(): void {
    Object.values(this.mediaRecorders).forEach((recorder) => {
      recorder.stop();
    });
  }

  public waitForStopped(): Promise<void> {
    return this.mediaRecordersStopped;
  }

  private createEmptyAudioChunks(): Record<MediaRecorderType, Blob[]> {
    return Object.keys(this.mediaRecorders).reduce(
      (acc, type) => {
        const mediaRecorderType = type as MediaRecorderType;
        acc[mediaRecorderType] = [];
        return acc;
      },
      {} as Record<MediaRecorderType, Blob[]>
    );
  }

  private createAudioBlobs(): Record<MediaRecorderType, Blob> {
    return Object.entries(this.mediaRecorders).reduce(
      (acc, [type, recorder]) => {
        const mediaRecorderType = type as MediaRecorderType;
        acc[mediaRecorderType] = new Blob(this.audioChunks[mediaRecorderType], {
          type: recorder.mimeType,
        });
        return acc;
      },
      {} as Record<MediaRecorderType, Blob>
    );
  }

  private handleRecorderStop(mediaRecorderType: MediaRecorderType, callbacks: MultiAudioRecorderCallbacks): void {
    this.stoppedRecorders.add(mediaRecorderType);

    const totalRecorders = Object.keys(this.mediaRecorders).length;
    if (this.stoppedRecorders.size === totalRecorders) {
      callbacks.onstop(this.createAudioBlobs());
      this.stoppedRecorders.clear();
      this.mediaRecordersStoppedResolve?.();
    }
  }
}
